import { format } from "date-fns";

import {
  InvestorFundDetails,
  Options,
  YES,
  CartItems,
  Option,
  TransactionType,
  CartItemsMap,
  SwitchInOptions,
  SipSchemeOptions,
} from "../store/types";
import { UmrnOptions } from "../store/actions";

type GetFundOptions = (fundDetails: InvestorFundDetails[]) => Options;

type GetFolioOptions = (
  fundDetails: InvestorFundDetails[],
  fundCode: string
) => Options;

type GetSelectedCartItem = <T extends keyof CartItemsMap>(
  cartItems: CartItems<T>,
  state: { cartItemId?: string | number } | undefined
) => CartItems<T>[number];

type MapCartItemsToFundCode = (cartItems: CartItems<TransactionType>) => {
  [fundCode: string]: CartItems<TransactionType>;
};

type GetSchemesList = (
  fundCode: string,
  folio: number | string,
  fundDetails: InvestorFundDetails[]
) => Array<InvestorFundDetails & Option>;

interface GetSipSchemesList {
  (
    fundCode: string,
    folio: number | string,
    fundDetails: Array<InvestorFundDetails>
  ): SipSchemeOptions;
}

type GetSchemeDetails = (
  arg1:
    | SwitchInOptions[number]
    | ReturnType<typeof getAdditionalPurchaseSchemesFromFolio>[number]
    | SipSchemeOptions[number]
) => {
  label: string;
  scheme: string;
  plan: string;
  option: string;
  minAmount: string | number;
};

export const getFundOptions: GetFundOptions = function (fundDetails) {
  return fundDetails.reduce<Options>(getFundOptionsReducer, []);
};

function getFundOptionsReducer(
  acc: Options,
  curr: InvestorFundDetails
): Options {
  if (acc.find(({ value }) => value === curr.Fund)) return acc;

  const fundOption = { label: curr.FundDesc, value: curr.Fund };

  return [...acc, fundOption];
}

export const getFolioOptions: GetFolioOptions = function (
  fundDetails,
  fundCode
) {
  const folios = fundDetails.reduce((acc: string[], curr) => {
    if (curr.Fund.toString() !== fundCode.toString()) return acc;
    if (curr.pFlag !== YES) return acc;
    const folioNumber = curr.Acno.toString();
    return Array.from(new Set([...acc, folioNumber]));
  }, []);
  return folios.map((acno) => ({
    label: acno,
    value: acno,
  }));
};

export const getSelectedCartItem: GetSelectedCartItem = function (
  cartItems,
  state
) {
  if (!state) return cartItems[0];

  const selectedCartItem = cartItems.find(
    ({ cartItemId }) => cartItemId === state.cartItemId
  );

  if (!selectedCartItem) return cartItems[0];

  return selectedCartItem;
};

export const mapCartItemsToFundCodes: MapCartItemsToFundCode = function (
  cartItems
) {
  const fundCodes = Array.from(
    new Set(cartItems.map((cartItem) => cartItem.fund.toString()))
  );

  return fundCodes.reduce((acc, curr) => {
    const fundCartItems = cartItems.filter(
      ({ fund }) => fund.toString() === curr
    );
    return { ...acc, [curr]: fundCartItems };
  }, {});
};

export const getSchemesList: GetSchemesList = function (
  fundCode,
  folio,
  fundDetails
) {
  return fundDetails
    .filter((fund) =>
      [
        fund.rSchFlg === YES,
        fund.Acno.toString() === folio.toString(),
        fund.Fund.toString() === fundCode.toString(),
      ].every(Boolean)
    )
    .map((item) => ({ ...item, label: item.SchDesc, value: item.SchDesc }));
};

export const getSipSchemesFromFolios: GetSipSchemesList = function (
  fundCode,
  folio,
  fundDetails
) {
  return fundDetails
    .filter((fund) =>
      [
        fund.rSchFlg === YES,
        fund.Acno.toString() === folio.toString(),
        fund.Fund.toString() === fundCode.toString(),
      ].every(Boolean)
    )
    .map((item) => ({
      combo_disclaimer: item.combo_disclaimer,
      combo_flag: item.combo_flag,
      label: `${item.SchDesc1} - ${item.PlnDesc} - ${item.OptDesc}`,
      value: `${item.SchDesc}-${item.PlnDesc}-${item.OptDesc}`,
      multiples: item.multiples,
      option: item.Opt,
      plan: item.Pln,
      scheme: item.Sch,
      sipMinAmount: item.sipminamt,
      newPurchaseMinAmount: item.MinAmt,
    }));
};

export const getAdditionalPurchaseSchemesFromFolio: GetSchemesList = function (
  fundCode,
  folio,
  fundDetails
) {
  return fundDetails
    .filter((fund) =>
      [
        fund.pSchFlg === YES,
        fund.Acno.toString() === folio.toString(),
        fund.Fund.toString() === fundCode.toString(),
      ].every(Boolean)
    )
    .map((item) => ({
      ...item,
      label: `${item.SchDesc1}-${item.PlnDesc}-${item.OptDesc}`,
      value: `${item.SchDesc}-${item.PlnDesc}-${item.OptDesc}`,
    }));
};

export const getSipSchemesFromFolio: GetSchemesList = function (
  fundCode,
  folio,
  fundDetails
) {
  return fundDetails
    .filter((fund) =>
      [
        fund.sipSchFlg === YES,
        fund.Acno.toString() === folio.toString(),
        fund.Fund.toString() === fundCode.toString(),
      ].every(Boolean)
    )
    .map((item) => ({
      ...item,
      label: `${item.SchDesc1} - ${item.PlnDesc} - ${item.OptDesc}`,
      value: `${item.SchDesc}-${item.PlnDesc}-${item.OptDesc}`,
    }));
};

export const getSchemeDetails: GetSchemeDetails = function (selectedScheme) {
  if ("Sch" in selectedScheme) {
    return {
      scheme: selectedScheme.Sch,
      plan: selectedScheme.Pln,
      option: selectedScheme.Opt,
      label: selectedScheme.label,
      minAmount: selectedScheme.sipminamt,
    };
  }
  if ("fm_scheme" in selectedScheme) {
    return {
      scheme: selectedScheme.fm_scheme,
      plan: selectedScheme.fm_plan,
      label: selectedScheme.label,
      option: selectedScheme.fm_option,
      minAmount: selectedScheme.SIPminamt,
    };
  } else {
    return {
      scheme: selectedScheme.scheme,
      plan: selectedScheme.plan,
      option: selectedScheme.option,
      minAmount: selectedScheme.sipMinAmount,
      label: selectedScheme.label,
    };
  }
};

export function handleError(error: unknown) {
  if (error instanceof Error) {
    alert(error.message);
  } else {
    alert(error);
  }
  console.trace(error);
}

export const convertToInr = function (value: string | number) {
  if (!value) return "";
  return parseInt(value.toString().replaceAll(",", "")).toLocaleString(
    "en-IN",
    {
      maximumFractionDigits: 2,
      style: "currency",
      currency: "INR",
    }
  );
};

export const getDescriptiveDate = function (date: Date | string) {
  return new Date(date).toLocaleDateString("en-IN", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
};

export const convertDateToIndian = function (date: Date | string) {
  return new Date(date).toLocaleDateString("en-IN", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};
export const convertDate = function (date: string) {
  if (date?.length > 10) {
    return format(new Date(date), "dd/MM/yyyy");
  }

  const [day, month, year] = date.split("/");
  return `${month}/${day}/${year}`;
};

export function getSipAmountAndDates(args: CartItems<"SIN">) {
  const startDates = args.map((sip) => {
    return new Date(sip.payload.sipStartDate).getTime();
  });
  const endDates = args.map((sip) => {
    return new Date(sip.payload.sipEndDate).getTime();
  });

  const amounts = args.map((sip) => sip.payload.installmentAmount);

  const earliestDate = new Date(Math.min(...startDates));
  const latestDate = new Date(Math.max(...endDates));
  const greatestAmount = Math.max(...amounts);

  return {
    earliestDate,
    latestDate,
    greatestAmount,
  };
}

export function isValidMandateFilterLumpsum(amount: number) {
  return function (mandateArgs: UmrnOptions[number]) {
    if (!mandateArgs) return false;
    const { mandateLimit, startDate, endDate } = mandateArgs;

    if (mandateLimit < amount) return false;

    return (
      new Date(convertDate(startDate)).getTime() <= new Date().getTime() &&
      new Date(convertDate(endDate)).getTime() > new Date().getTime()
    );
  };
}

export function isValidMandateFilter(
  mandateArgs: UmrnOptions[number],
  sipArgs: ReturnType<typeof getSipAmountAndDates>,
  payload:any
) {
  const { startDate, endDate, mandateLimit, value } = mandateArgs;
  const { earliestDate, greatestAmount, latestDate } = sipArgs;

  const isStartDateValid = new Date(convertDate(startDate)) <= earliestDate;
  const freq = payload.sipFrequency?.toUpperCase() as "M" | "Q";
  const multiplier = { M: 1, Q: 3 }[freq];
  const requiredEndDate = payload.sipAutoRenewal
    ? new Date(
        new Date(latestDate).setMonth(
          new Date(latestDate).getMonth() +
            +payload.noOfInstallments * multiplier
        )
      )
    : latestDate;
  const isEndDateValid = new Date(convertDate(endDate)) >= requiredEndDate;
  const isAmountValid = mandateLimit >= greatestAmount;

  return (
    value === "createMandate" ||
    [isStartDateValid, isEndDateValid, isAmountValid].every(Boolean)
  );
}

export const getUpdatedValues = function <Values>(
  FIELD_NAMES: Array<keyof Values>
) {
  return function (
    values: Values,
    key: keyof Values,
    fieldValues: Partial<Values>
  ) {
    const index = FIELD_NAMES.findIndex((fieldName) => fieldName === key);
    const updatedValues = FIELD_NAMES.slice(index).reduce<Partial<Values>>(
      (acc, curr) => {
        return { ...acc, [curr]: "" };
      },
      {}
    );

    return { ...values, ...updatedValues, ...fieldValues };
  };
};

export function isNotAotm(o: Option) {
  return o.value !== "AOTM";
}
